import { isMemIngestionEchoForModelData } from "./ingestion-echo";

export type LastActionVerb = "created" | "edited" | "shared";

// Tie-break priority when two actions carry the exact same timestamp. Higher
// wins. "shared" must beat "edited" (and "created") so that a note shared at the
// same instant it was last edited surfaces as "shared". Equal timestamps are
// otherwise rare — the echo buffer already separates Mem's create-then-edit
// bursts — but when they do coincide this makes the winner deterministic.
const VERB_TIE_BREAK_PRIORITY: Record<LastActionVerb, number> = {
  created: 0,
  edited: 1,
  shared: 2,
};

/**
 * The winning "last action" before contact/isMe resolution: which verb, by
 * which space account, at what time. Presentation-agnostic — callers resolve
 * actorId to a display name and decide isMe.
 */
export interface ResolvedLastAction {
  verb: LastActionVerb;
  actorId: string;
  at: string;
}

/**
 * The subset of note model data this resolver reads. Mirrors what
 * NoteObservable.lastAction consumes (minus the store-derived collection add,
 * which is passed in separately via collectionAdd).
 *
 * Id fields are widened to allow null so the same shape can be built from a
 * NoteObservable (non-null ids) or a server artifact payload (nullable ids).
 * The resolver tolerates missing ids via its eligibility filter.
 */
export type LastActionModelData = {
  attributed_created_by_space_account_id?: string | null;
  created_by_space_account_id?: string | null;
  owned_by_space_account_id?: string | null;
  last_modified_by_space_account_id?: string | null;
  locally_modified_at?: string | null;
  quota_created_at?: string | null;
  locally_created_at?: string | null;
  created_at?: string | null;
  shared_with_me_at?: string | null;
  shared_with_me_by_space_account_id?: string | null;
};

// Mirrors NoteObservable.attributedCreatorSpaceAccountId's fallback chain.
const getAttributedCreatorSpaceAccountId = (modelData: LastActionModelData): string | undefined =>
  modelData.attributed_created_by_space_account_id ??
  modelData.created_by_space_account_id ??
  modelData.owned_by_space_account_id ??
  undefined;

/**
 * Pure core of NoteObservable.lastAction: builds created/edited/shared
 * candidates, suppresses the Mem ingestion echo on the "edited" candidate, and
 * returns the most-recent eligible action. Shared so the chat note-artifact
 * preview produces identical results to the floating preview.
 *
 * `collectionAdd` is the latest non-me collection membership add (already
 * echo-filtered by the caller); the chat path has no collection data and omits
 * it.
 */
export const resolveLastAction = (params: {
  modelData: LastActionModelData;
  lastModifierSpaceAccountId: string | undefined;
  collectionAdd?: { actorId: string; at: string };
}): ResolvedLastAction | undefined => {
  const { modelData, lastModifierSpaceAccountId, collectionAdd } = params;

  const isEcho = lastModifierSpaceAccountId ? isMemIngestionEchoForModelData(modelData) : false;
  const editedActorId = isEcho ? undefined : lastModifierSpaceAccountId;

  const candidates: { verb: LastActionVerb; at?: string | null; actorId?: string | null }[] = [
    {
      verb: "created",
      at: modelData.created_at,
      actorId: getAttributedCreatorSpaceAccountId(modelData),
    },
    {
      verb: "edited",
      at: modelData.locally_modified_at,
      actorId: editedActorId,
    },
    {
      verb: "shared",
      at: modelData.shared_with_me_at ?? undefined,
      actorId: modelData.shared_with_me_by_space_account_id ?? undefined,
    },
  ];

  if (collectionAdd) {
    candidates.push({ verb: "edited", at: collectionAdd.at, actorId: collectionAdd.actorId });
  }

  const eligible = candidates.filter((c): c is ResolvedLastAction => !!c.at && !!c.actorId);

  // Most-recent action wins; on an exact timestamp tie, higher verb priority
  // wins (shared > edited > created). Comparison is by epoch-millis, not raw
  // string: the same instant can arrive in different ISO encodings across fields
  // (e.g. shared_with_me_at ships "…116Z" while locally_modified_at ships
  // "…116000+00:00"), and only an instant comparison routes those genuine ties
  // through the verb tie-break instead of accidental lexical order.
  return eligible.reduce<ResolvedLastAction | undefined>((best, c) => {
    if (!best) return c;
    const cMs = new Date(c.at).getTime();
    const bestMs = new Date(best.at).getTime();
    if (cMs > bestMs) return c;
    if (cMs === bestMs && VERB_TIE_BREAK_PRIORITY[c.verb] > VERB_TIE_BREAK_PRIORITY[best.verb]) {
      return c;
    }
    return best;
  }, undefined);
};
